import styled from "styled-components";

import {
  ACCENT,
  ACCENT_DARK,
  ACCENT_TINT,
  INK,
  BODY,
  MUTED,
  FAINT,
  LINE,
  HAIRLINE,
  SURFACE,
} from "./tokens";

// Shared building blocks for the document scanning pages. Every page uses
// these rather than its own one-off styled components, so the section stays
// consistent as pages are added.

export const Head = styled.div`
  background: ${SURFACE};
  border-bottom: 1px solid ${LINE};
  padding: 26px 0 52px;
`;

export const Crumbs = styled.nav`
  font-size: 13px;
  color: ${MUTED};
  margin-bottom: 28px;

  a {
    color: ${MUTED};
    text-decoration: none;

    &:hover {
      color: ${ACCENT};
      text-decoration: none;
    }
  }

  span.sep {
    margin: 0 8px;
    color: #cbd5e1;
  }

  span.here {
    color: ${INK};
    font-weight: 500;
  }
`;

export const H1 = styled.h1`
  margin: 0 0 16px;
  font-size: 38px;
  line-height: 1.1;
  letter-spacing: -1.4px;
  font-weight: 700;
  color: ${INK};

  @media (min-width: 768px) {
    font-size: 46px;
    letter-spacing: -1.7px;
  }
`;

export const Lede = styled.p`
  margin: 0;
  font-size: 18px;
  line-height: 1.62;
  color: ${BODY};
  max-width: 640px;
`;

export const H2 = styled.h2`
  margin: 0 0 16px;
  font-size: 28px;
  line-height: 1.18;
  letter-spacing: -0.9px;
  font-weight: 700;
  color: ${INK};
`;

export const H3 = styled.h3`
  margin: 0 0 8px;
  font-size: 17px;
  line-height: 1.35;
  font-weight: 600;
  color: ${INK};
`;

export const P = styled.p`
  margin: 0 0 16px;
  font-size: 16px;
  line-height: 1.64;
  color: ${BODY};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const Small = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: ${MUTED};
`;

export const Eyebrow = styled.div`
  font-size: 12px;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${ACCENT};
  font-weight: 600;
  margin-bottom: 14px;
`;

export const Card = styled.div`
  height: 100%;
  padding: 24px;
  border: 1px solid ${LINE};
  border-radius: 12px;
  background: #ffffff;
`;

export const Panel = styled.div`
  padding: 24px;
  border: 1px solid ${LINE};
  border-radius: 12px;
  background: #ffffff;
`;

export const Callout = styled.div`
  border-left: 3px solid ${ACCENT};
  padding: 2px 0 2px 22px;
`;

export const Tint = styled.div`
  background: ${ACCENT_TINT};
  border-radius: 12px;
  padding: 28px;

  p {
    color: ${ACCENT_DARK};
  }

  h2,
  h3 {
    color: ${ACCENT_DARK};
  }
`;

export const Warn = styled.div`
  border: 2px solid #f59e0b;
  background: #fffbeb;
  border-radius: 12px;
  padding: 26px;

  h2,
  h3 {
    color: #78350f;
  }

  p {
    color: #78350f;
  }

  a {
    color: #78350f;
    text-decoration: underline;
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  span {
    font-size: 14px;
    border: 1px solid ${LINE};
    border-radius: 100px;
    padding: 6px 14px;
    color: ${BODY};
  }
`;

// Numbered process list. The marker is a rounded square rather than a circle
// so it does not read as a bullet at small sizes.
export const Steps = styled.ol`
  counter-reset: step;
  list-style: none;
  padding-left: 0;
  margin: 0;

  li {
    counter-increment: step;
    position: relative;
    padding-left: 58px;
    margin-bottom: 28px;
    color: ${BODY};
    font-size: 16px;
    line-height: 1.64;

    &:last-child {
      margin-bottom: 0;
    }

    &::before {
      content: counter(step);
      position: absolute;
      left: 0;
      top: -1px;
      width: 38px;
      height: 38px;
      line-height: 38px;
      text-align: center;
      border-radius: 9px;
      font-weight: 700;
      font-size: 16px;
      color: ${ACCENT};
      background: ${ACCENT_TINT};
    }

    b {
      display: block;
      color: ${INK};
      font-size: 17px;
      font-weight: 600;
      margin-bottom: 5px;
    }
  }
`;

export const Faq = styled.div`
  h3 {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.35;
    margin: 0 0 9px;
    color: ${INK};
  }

  p {
    color: ${BODY};
    font-size: 16px;
    line-height: 1.64;
    margin: 0 0 30px;
  }

  div:last-child p {
    margin-bottom: 0;
  }
`;

// Rate tables scroll sideways on a phone rather than squashing: the price
// column has to stay readable.
export const Scroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const Table = styled.table`
  width: 100%;
  min-width: 420px;
  margin: 0;
  border-collapse: collapse;

  th,
  td {
    padding: 13px 16px;
    border-bottom: 1px solid ${HAIRLINE};
    text-align: left;
    vertical-align: top;
    font-size: 15px;
  }

  thead th {
    background: ${SURFACE};
    font-size: 11px;
    letter-spacing: 0.9px;
    text-transform: uppercase;
    color: ${FAINT};
    font-weight: 600;
    border-bottom: 1px solid ${LINE};
    white-space: nowrap;
  }

  td {
    color: ${BODY};
  }

  td.key {
    color: ${INK};
  }

  td.num,
  th.num {
    text-align: right;
    white-space: nowrap;
  }

  td.price {
    text-align: right;
    white-space: nowrap;
    color: ${ACCENT};
    font-weight: 600;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

// The headline price block.
export const Rate = styled.div`
  .from {
    font-size: 12px;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: ${FAINT};
    font-weight: 600;
    margin-bottom: 10px;
  }

  .row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 12px;
  }

  .amount {
    font-size: 42px;
    font-weight: 700;
    letter-spacing: -1.6px;
    color: ${INK};
    line-height: 1;
  }

  .unit {
    font-size: 16px;
    color: ${MUTED};
  }
`;

// data-dim rather than a prop: styled-components v4 forwards unknown props to
// the DOM and React warns about them.
export const Mono = styled.div`
  font-size: 14px;
  line-height: 2;
  color: ${INK};
  word-break: break-word;

  &[data-dim="true"] {
    color: ${FAINT};
  }
`;
